import type {
  RecencyFilter,
  RouteBuilderProperty,
  RouteBuilderSession,
} from "../../../../../types/routeBuilder";
import type { UnassignedSort } from "../types/routeBuilderTypes";
import { compareNames } from "./routeGroups";
import { propertyMatchesSearch } from "./helpers";

export const UNASSIGNED_SORT_OPTIONS: Array<{
  value: UnassignedSort;
  label: string;
}> = [
  { value: "name", label: "Name" },
  { value: "recentlyEdited", label: "Recently Edited" },
];

export const NOT_ON_ROUTE = "__not_on_route__";

export type AddUnassignedFilters = {
  search: string;
  serviceTypes: string[];
  recency: RecencyFilter;
  routeIds: string[];
  sort: UnassignedSort;
};

export function defaultAddUnassignedFilters(): AddUnassignedFilters {
  return {
    search: "",
    serviceTypes: [],
    recency: null,
    routeIds: [],
    sort: "name",
  };
}

export type SavedCatalogRoute = {
  _id: string;
  name: string;
  season: RouteBuilderSession["inputs"]["season"];
};

export function addUnassignedRouteOptions(
  saved: SavedCatalogRoute[],
  session: RouteBuilderSession,
) {
  const routes = new Map(
    saved
      .filter((route) => route.season === session.inputs.season)
      .map((route) => [route._id, { value: route._id, label: route.name }]),
  );
  for (const route of session.routes) {
    const id = session.routePersistence?.[route.id]?._id ?? route.id;
    if (!routes.has(id)) routes.set(id, { value: id, label: route.name });
  }
  return [
    { value: NOT_ON_ROUTE, label: "Not on a route" },
    ...Array.from(routes.values()).sort((a, b) =>
      compareNames(a.label, b.label),
    ),
  ];
}

export function selectAddUnassignedCatalog(
  properties: RouteBuilderProperty[],
  session: Pick<RouteBuilderSession, "routes" | "routePersistence">,
  {
    serviceTypes,
    routeIds,
    search,
  }: Pick<AddUnassignedFilters, "serviceTypes" | "routeIds" | "search">,
): RouteBuilderProperty[] {
  const sessionRoutes = new Map<string, Set<string>>();
  for (const route of session.routes) {
    for (const stop of route.stops) {
      const ids = sessionRoutes.get(stop.propertyId) ?? new Set<string>();
      ids.add(session.routePersistence?.[route.id]?._id ?? route.id);
      sessionRoutes.set(stop.propertyId, ids);
    }
  }
  const seen = new Set<string>();
  return properties.filter((property) => {
    if (seen.has(property.jobId)) return false;
    seen.add(property.jobId);
    if (
      serviceTypes.length &&
      !(
        property.serviceTypes?.length
          ? property.serviceTypes
          : [property.serviceType]
      ).some((service) => serviceTypes.includes(service))
    )
      return false;
    if (!propertyMatchesSearch(property, search)) return false;
    if (routeIds.length === 0) return true;
    // Saved membership and local placement are independent, so match their union.
    const assigned = new Set([
      ...(property.assignedRoutes ?? []).map((route) => route.id),
      ...Array.from(sessionRoutes.get(property.jobId) ?? []),
    ]);
    return routeIds.some((id) =>
      id === NOT_ON_ROUTE ? assigned.size === 0 : assigned.has(id),
    );
  });
}

/** Only session members render in the panel; searching never changes their saved order. */
export function unassignedVisits(
  session: Pick<RouteBuilderSession, "unroutedJobs" | "availableProperties">,
  search = "",
) {
  const properties = new Map(
    session.availableProperties.map((property) => [property.jobId, property]),
  );
  return session.unroutedJobs
    .filter((job) => {
      const property = properties.get(job.jobId);
      return propertyMatchesSearch(property ?? { name: job.name }, search);
    })
    .map((job) => ({
      propertyVisitId: job.propertyVisitId,
      propertyId: job.jobId,
    }));
}

export function selectableCatalogProperties(
  properties: RouteBuilderProperty[],
  unroutedJobs: RouteBuilderSession["unroutedJobs"],
) {
  const pooled = new Set(unroutedJobs.map((job) => job.jobId));
  return properties.filter((property) => !pooled.has(property.jobId));
}

/**
 * Put the services that match the Service type filter first on each property,
 * so its card shows why it's listed. A property can have several services
 * ticked and only needs one of them to match.
 */
export function withMatchingServicesFirst(
  properties: RouteBuilderProperty[],
  selected: string[],
): RouteBuilderProperty[] {
  if (selected.length === 0) return properties;
  return properties.map((property) => {
    const services = property.serviceTypes?.length
      ? property.serviceTypes
      : [property.serviceType];
    const matching = services.filter((service) => selected.includes(service));
    if (matching.length === 0 || matching[0] === services[0]) return property;
    return {
      ...property,
      serviceTypes: [
        ...matching,
        ...services.filter((service) => !selected.includes(service)),
      ],
    };
  });
}

/** Missing or invalid save timestamps sort after every dated property. */
function lastEditedAt(
  property: Pick<RouteBuilderProperty, "updatedAt">,
): number {
  const saved = Date.parse(property.updatedAt ?? "");
  return Number.isNaN(saved) ? -Infinity : saved;
}

/** Order for the Add properties popup. "Recently Edited" breaks ties by name. */
export function sortAddUnassignedCatalog(
  properties: RouteBuilderProperty[],
  sort: UnassignedSort,
): RouteBuilderProperty[] {
  if (sort === "name") {
    return [...properties].sort((a, b) => compareNames(a.name, b.name));
  }
  return properties
    .map((property) => ({ property, editedAt: lastEditedAt(property) }))
    .sort(
      (a, b) =>
        b.editedAt - a.editedAt ||
        compareNames(a.property.name, b.property.name),
    )
    .map(({ property }) => property);
}
